import { GetInvoiceStatsExcelDto } from "@/types/api/api-types";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";

export function useDownloadInvoiceStatsExcel(
  invoiceStatsExcel: GetInvoiceStatsExcelDto | undefined,
) {
  const { t } = useTranslation("boInvoices");

  return () => {
    if (!invoiceStatsExcel) return;
    // En-têtes de colonnes de l'export (texte affiché dans le fichier Excel)
    const col = {
      id: t("statsExcel.columns.id"),
      billingEntity: t("statsExcel.columns.billingEntity"),
      amountToBill: t("statsExcel.columns.amountToBill"),
      totalHt: t("statsExcel.columns.totalHt"),
      totalTva: t("statsExcel.columns.totalTva"),
      adjustment: t("statsExcel.columns.adjustment"),
      transportHt: t("statsExcel.columns.transportHt"),
      packaging: t("statsExcel.columns.packaging"),
      packagingProtection: t("statsExcel.columns.packagingProtection"),
      taxes: t("statsExcel.columns.taxes"),
      insurance: t("statsExcel.columns.insurance"),
      region: t("statsExcel.columns.region"),
      country: t("statsExcel.columns.country"),
      countryCode: t("statsExcel.columns.countryCode"),
      tva: t("statsExcel.columns.tva"),
    };
    const invoices = invoiceStatsExcel.invoices || [];
    const customs = invoiceStatsExcel.customs || [];
    const invoiceSheet = invoices
      .sort((a, b) => a.invoiceId.localeCompare(b.invoiceId))
      .map((inv: any) => ({
        [col.id]: inv.invoiceId,
        [col.billingEntity]: inv.billingEntity?.companyName ?? "",
        [col.amountToBill]: inv.amountToBill / 100,
        [col.totalHt]: inv.totalHT / 100,
        [col.totalTva]: inv.totalTva / 100,
        [col.adjustment]: inv.adjustment / 100,
        [col.transportHt]: inv.transportHt / 100,
        [col.packaging]: inv.packaging / 100,
        [col.packagingProtection]: inv.packagingProtection / 100,
        [col.taxes]: inv.taxes / 100,
        [col.insurance]: inv.insurance / 100,
      }));

    const customsMap: Record<string, any> = {};

    invoices.forEach((invoice) => {
      const customsForInvoice: (GetInvoiceStatsExcelDto["customs"][0] | null)[] =
        invoice.expeditions
          .map(({ expedition }) => {
            const customId = expedition?.quotation?.customId;
            return (
              customs.find((c: GetInvoiceStatsExcelDto["customs"][0]) => c.id === customId) || null
            );
          })
          .filter((c: GetInvoiceStatsExcelDto["customs"][0] | null) => c !== null);
      customsForInvoice
        .filter((c) => c !== null)
        .forEach((custom) => {
          if (!custom) return;
          const label = custom.label;
          const codePays =
            custom.countryCode && custom.countryCode.length > 0 ? custom.countryCode[0] : "";
          const key = custom.id;
          if (!customsMap[key]) {
            customsMap[key] = {
              [col.region]: custom.region,
              [col.country]: label,
              [col.countryCode]: codePays,
              [col.transportHt]: 0,
              [col.packaging]: 0,
              [col.packagingProtection]: 0,
              [col.taxes]: 0,
              [col.insurance]: 0,
              [col.tva]: 0,
            };
          }
        });

      try {
        invoice.expeditions.forEach(({ expedition, expeditionInvoiceData }) => {
          if (!expedition.quotation?.customId) return;
          const customId = expedition.quotation?.customId;
          customsMap[customId][col.transportHt] += expeditionInvoiceData.transportHt;
          customsMap[customId][col.packaging] += expeditionInvoiceData.packaging;
          customsMap[customId][col.packagingProtection] +=
            expeditionInvoiceData.packagingProtection;
          customsMap[customId][col.taxes] += expeditionInvoiceData.taxes;
          customsMap[customId][col.insurance] += expeditionInvoiceData.insurance;
          customsMap[customId][col.tva] += expeditionInvoiceData.tva;
        });
      } catch (error) {
        console.error("error", invoice, error);
      }
    });
    const customsMapSorted = Object.values(customsMap).sort((a, b) => {
      if (a[col.region] !== b[col.region]) {
        return a[col.region].localeCompare(b[col.region]);
      }
      return a[col.country].localeCompare(b[col.country]);
    });
    const paysSheet = Object.values(customsMapSorted);

    const wb = XLSX.utils.book_new();
    const ws1 = XLSX.utils.json_to_sheet(invoiceSheet);
    XLSX.utils.book_append_sheet(wb, ws1, t("statsExcel.sheets.invoices"));
    if (paysSheet.length > 0) {
      const ws4 = XLSX.utils.json_to_sheet(paysSheet);
      XLSX.utils.book_append_sheet(wb, ws4, t("statsExcel.sheets.countries"));
    }

    XLSX.writeFile(wb, `${t("statsExcel.fileName")}.xlsx`);
  };
}
